import { useEffect, useCallback } from "react";
import { useBlocker } from "react-router-dom";

interface UseNavigationGuardOptions {
  when: boolean;
  message?: string;
  onBeforeUnload?: () => void;
  onNavigationBlocked?: () => boolean;
}

/**
 * Custom hook to guard navigation away from a route
 * @param options Configuration options for the navigation guard
 */
export const useNavigationGuard = ({
  when,
  message = "Are you sure you want to leave? Your progress may be lost.",
  onBeforeUnload,
  onNavigationBlocked,
}: UseNavigationGuardOptions) => {
  // Block navigation within the React Router
  const blocker = useBlocker(
    useCallback(
      (args: any) => {
        // Only block if the condition is met and we're actually navigating away
        return (
          when && args.currentLocation.pathname !== args.nextLocation.pathname
        );
      },
      [when]
    )
  );

  // Handle browser refresh/close/back button
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (when) {
        // Call custom callback if provided
        if (onBeforeUnload) {
          onBeforeUnload();
        }

        // Set the return value to show browser confirmation dialog
        event.preventDefault();
        // Modern browsers use preventDefault() and return value
        return message;
      }
    };

    if (when) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [when, message, onBeforeUnload]);

  // Handle the blocked navigation
  useEffect(() => {
    if (blocker.state === "blocked") {
      let shouldProceed = true;

      // Use custom handler if provided, otherwise use window.confirm
      if (onNavigationBlocked) {
        shouldProceed = onNavigationBlocked();
      } else {
        shouldProceed = window.confirm(message);
      }

      if (shouldProceed) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker, message, onNavigationBlocked]);

  return {
    isBlocked: blocker.state === "blocked",
    blocker,
  };
};

export default useNavigationGuard;
